import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bed, Bath, Users, DoorOpen } from 'lucide-react';
import type { PropertyLayout, BedType, RoomType } from '../types';

interface RoomDetailsProps {
  layout: PropertyLayout;
  compact?: boolean;
}

const RoomDetails: React.FC<RoomDetailsProps> = ({ layout, compact = false }) => {
  const { t } = useTranslation();

  const getBedTypeName = (type: BedType): string => {
    return t(`beds.${type}`);
  };

  const getRoomTypeName = (type: RoomType): string => {
    return t(`rooms.${type}`);
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <DoorOpen className="h-4 w-4" />
          {layout.totalBedrooms} {t('property.rooms')}
        </span>
        <span className="flex items-center gap-1">
          <Bed className="h-4 w-4" />
          {layout.totalBeds} {t('property.beds')}
        </span>
        <span className="flex items-center gap-1">
          <Bath className="h-4 w-4" />
          {layout.totalBathrooms} {t('property.bathrooms')}
        </span>
        <span className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          {t('property.maxGuests')}: {layout.maxGuests}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
          <DoorOpen className="h-6 w-6 text-primary" />
          <div>
            <p className="text-2xl font-bold">{layout.totalBedrooms}</p>
            <p className="text-sm text-muted-foreground">{t('property.rooms')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
          <Bed className="h-6 w-6 text-primary" />
          <div>
            <p className="text-2xl font-bold">{layout.totalBeds}</p>
            <p className="text-sm text-muted-foreground">{t('property.beds')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
          <Bath className="h-6 w-6 text-primary" />
          <div>
            <p className="text-2xl font-bold">{layout.totalBathrooms}</p>
            <p className="text-sm text-muted-foreground">{t('property.bathrooms')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
          <Users className="h-6 w-6 text-primary" />
          <div>
            <p className="text-2xl font-bold">{layout.maxGuests}</p>
            <p className="text-sm text-muted-foreground">{t('property.maxGuests')}</p>
          </div>
        </div>
      </div>

      {/* Room details */}
      {layout.rooms && layout.rooms.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium">Room Details</h4>
          <div className="grid gap-3">
            {layout.rooms.map((room, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{room.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {getRoomTypeName(room.type)}
                  </span>
                </div>
                {room.beds && room.beds.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {room.beds.map((bed, bedIndex) => (
                      <span
                        key={bedIndex}
                        className="text-xs bg-muted px-2 py-1 rounded"
                      >
                        {bed.count}x {getBedTypeName(bed.type)}
                      </span>
                    ))}
                  </div>
                )}
                {room.description && (
                  <p className="text-sm text-muted-foreground mt-2">{room.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomDetails;
